import { removeAllToasts } from '@medly-components/core';
import { useMantaCareAPI } from 'hooks/useMantaCareAPI/useMantaCareAPI';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserMapRoute } from '../../../constants/routes';
import { MantaCareUser } from '../../../context/UserContext/types';
import { getQuestionnaireObject } from 'utils/questionnaire';
import { QuestionnaireObject } from 'types/questionnaire';
import { useMapParams } from 'hooks/useMapParams';

interface IUserDetailsState {
    user: MantaCareUser | undefined;
    questionnaire: QuestionnaireObject | undefined;
    loading: boolean;
    reload: () => void;
}

interface IUserDetailControllers {
    unAssignMap: (regionId: string) => void;
    viewMap: (zoneName: string, region: string) => void;
}

export type IUserDetailContext = IUserDetailsState & IUserDetailControllers;

const userDetailsDefaultState: IUserDetailsState = {
    user: undefined,
    questionnaire: undefined,
    loading: true,
    reload: () => {}
};

const userDetailContextDefaultValues: IUserDetailContext = {
    ...userDetailsDefaultState,
    unAssignMap: () => {},
    viewMap: () => {}
};

export const UserDetailContext = createContext<IUserDetailContext>(userDetailContextDefaultValues);

export const UserDetailContextProvider = ({ children }: { children: JSX.Element }) => {
    const [userDetails, setUserDetails] = useState<IUserDetailsState>(userDetailsDefaultState);
    const { getQuestionnaireDataForUser, getUser, unAssignRegions } = useMantaCareAPI();
    const { userId } = useMapParams();
    const navigate = useNavigate();

    const loadData = async () => {
        if (!userId) return;
        setUserDetails(data => ({ ...userDetailsDefaultState, ...data, loading: true }));
        const user = await getUser(userId);
        let questionnaire: QuestionnaireObject;

        if (user?.hasCompletedOnboardingQuestionnaire) {
            const questionnaireRes = await getQuestionnaireDataForUser(userId);
            if (questionnaireRes) {
                questionnaire = getQuestionnaireObject(questionnaireRes);
            }
        }

        setUserDetails(data => ({ ...data, user, questionnaire, loading: false }));
    };

    const unAssignMap = useCallback(
        regionId => {
            if (!userDetails.user) return;
            unAssignRegions(userDetails.user.id, [regionId], {
                onSuccess: async () => {
                    await loadData();
                }
            });
        },
        [userDetails]
    );

    const viewMap = useCallback(
        (zoneName: string, region: string) => {
            if (!userDetails.user) return;

            navigate(getUserMapRoute(userDetails.user.id, region, zoneName));
        },
        [userDetails, navigate]
    );

    useEffect(() => {
        loadData();
    }, [userId]);

    useEffect(() => {
        return () => removeAllToasts();
    }, []);

    return (
        <UserDetailContext.Provider value={{ ...userDetails, loading: userDetails.loading, unAssignMap, viewMap, reload: loadData }}>
            {children}
        </UserDetailContext.Provider>
    );
};
